import { useState, type MouseEvent } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/PersonOutline';
import SecurityIcon from '@mui/icons-material/SecurityOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { paths } from '@/routes/paths';

function initials(name?: string | null, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }
  return email?.[0]?.toUpperCase() ?? '?';
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const displayName =
    user?.name ?? ([user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email);

  const go = (path: string) => {
    setAnchor(null);
    navigate(path);
  };

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          size="small"
        >
          <Avatar src={user?.avatarUrl ?? undefined} sx={{ width: 34, height: 34 }}>
            {initials(displayName, user?.email)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user?.email}
          </Typography>
          {user?.isSuperAdmin && (
            <Chip label="Super admin" size="small" color="primary" sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => go(paths.profile)}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => go(paths.security)}>
          <ListItemIcon>
            <SecurityIcon fontSize="small" />
          </ListItemIcon>
          Security &amp; 2FA
        </MenuItem>
        <MenuItem onClick={() => go(paths.sessions)}>
          <ListItemIcon>
            <DevicesIcon fontSize="small" />
          </ListItemIcon>
          Sessions &amp; devices
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
